import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { post, put } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * StaffCreatePage — create/edit form for a single staff member.
 *
 * `staffId` is a URL route param (":staffId/edit"), not a prop — it must
 * be read with useParams(). It was previously destructured as a prop with
 * a `null` default, which the router never actually supplies, so every
 * `mode === "edit" && staffId` check silently evaluated to false: the
 * fetch-and-populate effect never ran, and the update PUT request would
 * have gone to ".../staff/null".
 *
 * Edit mode now uses the staff data passed via navigation state from the
 * list page to avoid unnecessary API calls. The list page provides complete
 * data including split firstName/lastName fields.
 * ---------------------------------------------------------------------- */

const StaffCreatePage = ({ mode = "create", user }) => {
  document.title = mode === "create" ? "Create Staff | Omaha Food Lovers" : "Edit Staff | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId } = useParams();
  const [loading, setLoading] = useState(false);
  const [originalDesignation, setOriginalDesignation] = useState("manager");

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && staffId) {
      const stateStaffData = location.state?.staffData;
      if (stateStaffData) {
        // Store the original designation
        setOriginalDesignation(stateStaffData.designation_type || "manager");
        // Use the data passed from the list page
        formik.setValues({
          first_name: stateStaffData.firstName || "",
          last_name: stateStaffData.lastName || "",
          email: stateStaffData.email || "",
          password: "",
          password_confirmation: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, staffId]);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    last_name: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must not exceed 50 characters"),
    email: Yup.string().required("Email is required").email("Please enter a valid email address"),
    password:
      mode === "create"
        ? Yup.string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
              "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
            )
        : Yup.string(),
    password_confirmation: Yup.string().test("password-match", "Passwords must match", function (value) {
      const { password } = this.parent;
      if (!password || password.length === 0) return true;
      return value === password;
    }),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        first_name: true,
        last_name: true,
        email: true,
        password: true,
        password_confirmation: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) return;

      setLoading(true);

      try {
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          designation_type: originalDesignation === "admin" ? "admin" : "manager",
        };

        if (values.password) {
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
        }

        let response;
        if (mode === "create") {
          response = await post(endpoints.restaurantDashboardStaff, payload);
        } else if (mode === "edit" && staffId) {
          response = await put(`${endpoints.restaurantDashboardStaff}/${staffId}`, payload);
        }

        if (response?.data?.success) {
          toastr.success(
            mode === "create" ? "Staff member created successfully" : "Staff member updated successfully"
          );
          navigate("/restaurant/staff");
        }
      } catch (err) {
        console.error("Failed to save staff:", err);
        if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else {
          toastr.error(extractErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {mode === "create" ? "Create Staff Member" : "Edit Staff Member"}
            </h2>
            <p className="rp-page-header__subtitle">
              {mode === "create" ? "Add a new staff member to your restaurant" : "Update staff member information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  type="text"
                  placeholder="Enter first name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.first_name && !!formik.errors.first_name}
                  errorText={formik.errors.first_name}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  type="text"
                  placeholder="Enter last name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.last_name && !!formik.errors.last_name}
                  errorText={formik.errors.last_name}
                  className="white-bg"
                />
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                <CustomInput
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.email && !!formik.errors.email}
                  errorText={formik.errors.email}
                  className="white-bg"
                  disabled={mode === "edit"}
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="password"
                  label={mode === "edit" ? "Password (leave blank to keep current)" : "Password"}
                  name="password"
                  type="password"
                  placeholder={mode === "edit" ? "Enter new password" : "Enter password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.password && !!formik.errors.password}
                  errorText={formik.errors.password}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="password_confirmation"
                  label={
                    mode === "edit" ? "Confirm Password (leave blank to keep current)" : "Confirm Password"
                  }
                  name="password_confirmation"
                  type="password"
                  placeholder={mode === "edit" ? "Confirm new password" : "Confirm password"}
                  value={formik.values.password_confirmation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.password_confirmation && !!formik.errors.password_confirmation
                  }
                  errorText={formik.errors.password_confirmation}
                  className="white-bg"
                />
              </Col>
            </Row>

              <div className="mt-2">
                <SecondaryButton
                  type="submit"
                  variant="orange"
                  disabled={loading}
                  loading={loading}
                  icon={tickIcon}
                  iconPosition="right"
                >
                  {loading ? "Saving..." : mode === "create" ? "Create Staff Member" : "Update Staff Member"}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  variant="light-grey"
                  onClick={() => navigate("/restaurant/staff")}
                  icon={cancelIcon}
                  iconPosition="right"
                  style={{ marginLeft: "10px" }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </Form>
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.Login.user,
});

export default connect(mapStateToProps, null)(StaffCreatePage);